import { useEffect, useState } from 'react';
import { Modal, Form, Input, message } from 'antd';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
	createIndustrySector,
	updateIndustrySector,
	IndustrySector,
	industrySectorKeys,
} from '@/libs/fleet-api/services/admin-industry-sector';

interface IndustrySectorFormModalProps {
	open: boolean;
	onClose: () => void;
	mode: 'create' | 'update';
	initialValues?: IndustrySector;
}

const IndustrySectorFormModal = ({
	open,
	onClose,
	mode,
	initialValues,
}: IndustrySectorFormModalProps) => {
	const [form] = Form.useForm();
	const queryClient = useQueryClient();
	const [isSubmitting, setIsSubmitting] = useState(false);

	useEffect(() => {
		if (open && initialValues && mode === 'update') {
			form.setFieldsValue(initialValues);
		} else {
			form.resetFields();
		}
	}, [open, initialValues, mode, form]);

	const createMutation = useMutation({
		mutationFn: createIndustrySector,
		onSuccess: () => {
			message.success('Industry Sector created successfully');
			queryClient.invalidateQueries({ queryKey: industrySectorKeys.all });
			onClose();
		},
		onError: (error) => {
			message.error('Failed to create Industry Sector');
			console.error(error);
		},
	});

	const updateMutation = useMutation({
		mutationFn: updateIndustrySector,
		onSuccess: () => {
			message.success('Industry Sector updated successfully');
			queryClient.invalidateQueries({ queryKey: industrySectorKeys.all });
			onClose();
		},
		onError: (error) => {
			message.error('Failed to update Industry Sector');
			console.error(error);
		},
	});

	const handleSubmit = async () => {
		try {
			setIsSubmitting(true);
			const values = await form.validateFields();

			if (mode === 'create') {
				await createMutation.mutateAsync(values);
			} else {
				await updateMutation.mutateAsync({
					...values,
					id: initialValues?.id,
				});
			}
		} catch (error) {
			console.error('Validation failed:', error);
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<Modal
			title={mode === 'create' ? 'Add Industry Sector' : 'Edit Industry Sector'}
			open={open}
			onCancel={onClose}
			onOk={handleSubmit}
			confirmLoading={isSubmitting}
			destroyOnClose
			width={800}
		>
			<Form
				form={form}
				layout="vertical"
			>
				<Form.Item
					name="portofolioGuideline"
					label="Portofolio Guideline"
					rules={[
						{ required: true, message: 'Please enter Portofolio Guideline' },
					]}
				>
					<Input />
				</Form.Item>
				<Form.Item
					name="ecosystem"
					label="Ecosystem"
					rules={[{ required: true, message: 'Please enter Ecosystem' }]}
				>
					<Input />
				</Form.Item>
				<Form.Item
					name="industryClass"
					label="Industry Class"
					rules={[{ required: true, message: 'Please enter Industry Class' }]}
				>
					<Input />
				</Form.Item>
				<div
					style={{
						display: 'grid',
						gridTemplateColumns: '1fr 1fr',
						gap: '16px',
					}}
				>
					<Form.Item
						name="sectorLevel1"
						label="Sector Level 1"
						rules={[{ required: true, message: 'Please enter Sector Level 1' }]}
					>
						<Input />
					</Form.Item>
					<Form.Item
						name="sectorLevel2"
						label="Sector Level 2"
						rules={[{ required: true, message: 'Please enter Sector Level 2' }]}
					>
						<Input />
					</Form.Item>
					<Form.Item
						name="sectorLevel3"
						label="Sector Level 3"
						rules={[{ required: true, message: 'Please enter Sector Level 3' }]}
					>
						<Input />
					</Form.Item>
					<Form.Item
						name="sectorLevel4"
						label="Sector Level 4"
						rules={[{ required: true, message: 'Please enter Sector Level 4' }]}
					>
						<Input />
					</Form.Item>
					<Form.Item
						name="sectorLevel5"
						label="Sector Level 5"
						rules={[{ required: true, message: 'Please enter Sector Level 5' }]}
					>
						<Input />
					</Form.Item>
					<Form.Item
						name="code"
						label="Code"
					>
						<Input placeholder="Optional Code" />
					</Form.Item>
				</div>
			</Form>
		</Modal>
	);
};

export default IndustrySectorFormModal;
